import {
	NotificationService,
	type SnackbarFixedProps,
	SnackbarVariantColors,
	useIntl,
	useSnackbarVariantIconColors
} from '@capsicum/common';
import React, { type CSSProperties, type FC, type SVGProps, type SyntheticEvent } from 'react';
import styled, { useTheme } from 'styled-components';

import { AlertIcon, CrossBoldIcon, HourglassIcon, PollIcon } from '../../icons';

const isIcon = (component: FC<SVGProps<SVGSVGElement>> | JSX.Element): component is FC<SVGProps<SVGSVGElement>> => {
	return (component as React.ComponentProps<FC<SVGProps<SVGSVGElement>>>)?.color !== undefined;
};

type SnackbarContent = {
	leadingContent?: FC<SVGProps<SVGSVGElement>> | JSX.Element;
	leftContent?: FC<SVGProps<SVGSVGElement>> | JSX.Element;
	rightIcon?: FC<SVGProps<SVGSVGElement>>;
	style?: CSSProperties;
	trailingContent?: FC<SVGProps<SVGSVGElement>> | JSX.Element;
};

export type SnackbarProps = SnackbarFixedProps & SnackbarContent;

const snackbarIcons = new Map<SnackbarProps['variant'], (props: SVGProps<SVGSVGElement>) => JSX.Element>([
	['success', PollIcon],
	['pending', HourglassIcon],
	['error', AlertIcon]
]);

/**
 * Snackbar component.
 * @param {string} props.testID TestID
 * @param {string} props.notificationId The ID of the notification
 * @param {string} props.actionText Action text, located to the right of the snackbar
 * @param {FC<SVGProps<SVGSVGElement>>} props.rightIcon The icon that should be displayed on snackbar end
 * @param {FC<SVGProps<SVGSVGElement>> | JSX.Element} props.leadingContent the content above the description
 * @param {string} props.title - the snackbar title
 * @param {string} props.description - the snackbar description
 * @param {FC<SVGProps<SVGSVGElement>> | JSX.Element} props.leftContent the content in the left container
 * @param {CSSProperties} props.style the UI style to be applied to the outmost container
 * @param {FC<SVGProps<SVGSVGElement>> | JSX.Element} props.trailingContent the content below the description
 * @param {SnackbarProps['variant']} props.variant - the snackbar variant
 * @param {() => void} props.onActivate The callback function to be called on snackbar click
 * @param {() => void} props.onActionActivate The callback function to be called on action text click
 * @param {() => void} props.onRightIconAcviate The callback function to be called on right icon click
 * @returns {JSX.Element | null} The Snackbar component
 */
export const Snackbar = ({
	$description,
	$title,
	actionText,
	description,
	leadingContent: LeadingContent,
	leftContent: LeftContent,
	notificationId,
	onActionActivate,
	onActivate,
	onRightIconActivate,
	rightIcon: RightIcon = CrossBoldIcon,
	style,
	testID,
	title,
	trailingContent: TrailingContent,
	variant,
	...props
}: SnackbarProps): JSX.Element => {
	const { $t } = useIntl();
	const { colors, sizing } = useTheme();
	const variantIconColor = useSnackbarVariantIconColors(variant);
	const VariantIcon = snackbarIcons.get(variant);

	const _onActionClickHandler = (e: SyntheticEvent) => {
		e.stopPropagation();
		onActionActivate?.();
	};

	const _onRightIconClickHandler = (e: SyntheticEvent) => {
		e.stopPropagation();
		if (RightIcon === CrossBoldIcon) {
			if (notificationId) {
				NotificationService.removeNotification(notificationId);
			}
		} else {
			onRightIconActivate?.();
		}
	};

	return (
		<S.Container
			$isClickable={!!onActivate}
			$variant={variant}
			data-testid={testID}
			style={style}
			onClick={onActivate}
			{...props}
		>
			<S.LeftSideContainer>
				{VariantIcon ? (
					<VariantIcon color={variantIconColor} height={sizing.large} width={sizing.large} />
				) : (
					LeftContent &&
					(isIcon(LeftContent) ? <LeftContent color={colors.icons.main.enabled} /> : LeftContent)
				)}
			</S.LeftSideContainer>
			<S.MiddleContainer>
				{LeadingContent &&
					(isIcon(LeadingContent) ? <LeadingContent color={colors.icons.main.enabled} /> : LeadingContent)}
				{(title || $title) && <S.Title>{title ?? $t($title!)}</S.Title>}
				<S.Description>{description ?? $t($description)}</S.Description>
				{TrailingContent &&
					(isIcon(TrailingContent) ? <TrailingContent color={colors.icons.main.enabled} /> : TrailingContent)}
			</S.MiddleContainer>
			<S.RightSideContainer>
				{actionText && (
					<S.ActionTextContainer $isClickable={!!onActionActivate} onClick={_onActionClickHandler}>
						<S.ActionText>{actionText}</S.ActionText>
					</S.ActionTextContainer>
				)}
				{RightIcon && (
					<S.RightIconContainer $isClickable={!!onRightIconActivate} onClick={_onRightIconClickHandler}>
						<RightIcon color={colors.icons.main.enabled} height={24} width={24} />
					</S.RightIconContainer>
				)}
			</S.RightSideContainer>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div<{ $isClickable: boolean; $variant: SnackbarProps['variant'] }>`
		width: 100%;
		display: flex;
		flex-direction: row;
		align-items: center;
		${({ theme }) => theme.border.radius.medium};
		${({ theme }) => theme.elevations.level1};
		padding: ${({ theme }) => `${theme.spacings.xsmall} ${theme.spacings.small}`};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};

		${({ $variant }) => $variant && SnackbarVariantColors[$variant]};
	`;
	export const LeftSideContainer = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		margin-right: ${({ theme }) => theme.spacings.small};
	`;

	export const MiddleContainer = styled.div`
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	`;

	export const Title = styled.p`
		margin: 0;
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Description = styled.p`
		margin: 0;
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const RightSideContainer = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		margin-left: ${({ theme }) => theme.spacings.small};
	`;

	export const ActionTextContainer = styled.div<{ $isClickable: boolean }>`
		margin-right: ${({ theme }) => theme.spacings.medium};
		cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
	`;

	export const ActionText = styled.div`
		${({ theme }) => theme.fonts.text.text2};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const RightIconContainer = styled.div<{ $isClickable: boolean }>`
		cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
	`;
}
